import type React from "react"
import { useEffect } from "react"

type OutsideClickHandler = (event: MouseEvent | TouchEvent) => void

/**
 * Hook to detect clicks outside of a component
 * Tracks mouse and touch events on the document and triggers callback when click occurs outside the referenced element
 * @param ref - React ref to the element to monitor for outside clicks
 * @param callback - Function to call when a click outside the element is detected
 */
export const useOutsideClick = (
    ref: React.RefObject<HTMLElement | null>,
    callback: OutsideClickHandler
): void => {
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null
            if (!ref.current || (target && ref.current.contains(target))) {
                return
            }
            callback(event)
        }

        document.addEventListener("mousedown", listener)
        document.addEventListener("touchstart", listener, { passive: true })

        return () => {
            document.removeEventListener("mousedown", listener)
            document.removeEventListener("touchstart", listener)
        }
    }, [ref, callback])
}
